import React, { useEffect, useState } from 'react';
import { Alert, Button, Descriptions, Form, Input, Skeleton, Tag, message } from 'antd';
import { Navigate, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import FeaturePage from '@/components/featurepage';
import PullToRefresh from '@/components/pulltorefresh';
import { cancelEmailChange, confirmEmailChange, confirmEmailVerification, fetchEmailChange, fetchMyProfile, requestEmailChange, requestEmailVerification } from '@/apis/account';
import { fetchUserInfo } from '@/apis/user';
import { logout } from '@/store/modules/user';
import usePrivateList from '@/utils/usePrivateList';
import usePrivateAction from '@/utils/usePrivateAction';
import useMobile from '@/utils/useMobile';
import { getRequestErrorMessage } from '@/utils/requestError';
import './index.scss';

const loadEmail = async () => {
  const [profile, change] = await Promise.all([fetchMyProfile(), fetchEmailChange()]);
  return { profile, pending: change.pending };
};
const emailError = error => [404, 405].includes(error.response?.status)
  ? '邮箱设置功能暂不可用，请联系管理员确认服务版本'
  : getRequestErrorMessage(error, '邮箱设置加载失败，请稍后重试');

// 凭证与当前密码仅保留在当前表单中，不写入 Redux 或浏览器存储。
const EmailForms = ({ data, load, refreshing }) => {
  const [verificationForm] = Form.useForm();
  const [changeForm] = Form.useForm();
  const [confirmForm] = Form.useForm();
  const [cancelForm] = Form.useForm();
  const [retryAt, setRetryAt] = useState(0);
  const { busy: submitting, run: runAction } = usePrivateAction();
  const run = (operation, onSuccess) => runAction(operation, onSuccess, emailError);
  const busy = submitting || refreshing;
  useEffect(() => {
    if (!retryAt) return;
    const timer = setTimeout(() => setRetryAt(0), Math.max(0, retryAt - Date.now()));
    return () => clearTimeout(timer);
  }, [retryAt]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { profile, pending } = data;
  useEffect(() => { confirmForm.resetFields(); cancelForm.resetFields(); }, [pending?.new_email, pending?.expires_at, confirmForm, cancelForm]);
  const refresh = () => { void load().catch(() => {}); void dispatch(fetchUserInfo()); };
  const passwordField = <Form.Item name="current_password" label="当前密码" rules={[{ required: true, message: '请输入当前密码' }]}><Input.Password autoComplete="current-password" maxLength={4096} /></Form.Item>;
  return <div className="emailSettings-forms">
    <section className="featurePage-panel emailSettings-section">
      <h2>当前邮箱</h2>
      <Descriptions column={1} items={[{ key: 'email', label: '邮箱', children: profile.email || '尚未绑定' }, { key: 'verified', label: '归属验证', children: <Tag color={profile.is_email_verified ? 'success' : 'warning'}>{profile.is_email_verified ? '已验证' : '未验证'}</Tag> }, ...(profile.email_verified_at ? [{ key: 'time', label: '验证时间', children: new Date(profile.email_verified_at).toLocaleString('zh-CN') }] : [])]} />
      {profile.email && !profile.is_email_verified && <>
        <Alert type="info" showIcon title="邮件凭证有效期为 1 小时，重发会使旧凭证失效。验证不会影响正常登录和发帖。" />
        <Button disabled={busy || Date.now() < retryAt} loading={busy} onClick={() => run(requestEmailVerification, result => {
          message.info(result.detail || '请求已接收，请检查当前邮箱'); setRetryAt(Date.now() + 60000); refresh();
        })}>发送验证邮件</Button>
        <Form form={verificationForm} layout="vertical" disabled={busy} onFinish={values => run(
          () => confirmEmailVerification(values.verification_token.trim()),
          () => { verificationForm.resetFields(); message.success('邮箱归属已验证'); refresh(); },
        )}>
          <Form.Item name="verification_token" label="验证邮件中的完整凭证" rules={[{ required: true, message: '请输入完整验证凭证' }]}><Input.Password autoComplete="off" maxLength={101} /></Form.Item>
          <Button type="primary" htmlType="submit" loading={busy}>确认邮箱归属</Button>
        </Form>
      </>}
    </section>
    <section className="featurePage-panel emailSettings-section">
      <h2>{profile.email ? '更换邮箱' : '绑定邮箱'}</h2>
      <Alert type="info" showIcon title={profile.email ? '更换邮箱需要当前密码及新旧邮箱各自的邮件凭证。' : '绑定邮箱需要当前密码及新邮箱的邮件凭证。'} description="确认成功后需要重新登录；确认之前当前邮箱保持不变。" />
      <Form form={changeForm} layout="vertical" disabled={busy} onFinish={values => run(() => requestEmailChange({ ...values, new_email: values.new_email.trim() }), result => {
        changeForm.resetFields(); message.info(result.detail || '请求已接收，请检查邮箱'); refresh();
      })}>
        <Form.Item name="new_email" label="新邮箱" rules={[{ required: true, message: '请输入新邮箱' }, { type: 'email', message: '请输入有效邮箱' }]}><Input autoComplete="email" maxLength={254} /></Form.Item>
        {passwordField}<Button type="primary" htmlType="submit" loading={busy}>发送确认邮件</Button>
      </Form>
    </section>
    {pending && <section className="featurePage-panel emailSettings-section">
      <h2>待确认的邮箱变更</h2>
      <Descriptions column={1} items={[{ key: 'email', label: '新邮箱', children: pending.new_email }, { key: 'expiry', label: '有效至', children: new Date(pending.expires_at).toLocaleString('zh-CN') }]} />
      <Form form={confirmForm} layout="vertical" disabled={busy} onFinish={values => run(() => confirmEmailChange({
        current_password: values.current_password, new_email_token: values.new_email_token.trim(),
        ...(pending.requires_old_email ? { old_email_token: values.old_email_token.trim() } : {}),
      }), () => {
        confirmForm.resetFields(); dispatch(logout()); message.success('邮箱已变更，请重新登录'); navigate('/login', { replace: true });
      })}>
        {passwordField}
        <Form.Item name="new_email_token" label="新邮箱邮件中的完整凭证" rules={[{ required: true, message: '请输入新邮箱凭证' }]}><Input.Password autoComplete="off" maxLength={101} /></Form.Item>
        {pending.requires_old_email && <Form.Item name="old_email_token" label="旧邮箱邮件中的完整凭证" rules={[{ required: true, message: '请输入旧邮箱凭证' }]}><Input.Password autoComplete="off" maxLength={101} /></Form.Item>}
        <Button type="primary" htmlType="submit" loading={busy}>确认变更并重新登录</Button>
      </Form>
      <Form form={cancelForm} layout="vertical" disabled={busy} onFinish={values => run(() => cancelEmailChange(values.current_password), () => {
        cancelForm.resetFields(); message.success('邮箱变更已取消'); refresh();
      })}>
        {passwordField}<Button danger htmlType="submit" loading={busy}>取消待确认变更</Button>
      </Form>
    </section>}
  </div>;
};

const EmailSettings = () => {
  const mobile = useMobile();
  const { data, loading, error, load, isAuthenticated, sessionKey } = usePrivateList(loadEmail, {}, emailError);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <FeaturePage embedded showDesktopBack={false} back="/personal/info" className="emailSettings" title="邮箱设置" subtitle="验证邮箱归属，管理邮箱绑定与变更。">
    <PullToRefresh enabled={mobile} disabled={loading} onRefresh={load} successMessage="邮箱设置已刷新">
      {!mobile && <div className="emailSettings-toolbar"><Button disabled={loading} onClick={() => load().catch(() => {})}>刷新</Button></div>}
      {error && <Alert type="error" showIcon title={error} action={<Button onClick={() => load().catch(() => {})}>重试</Button>} />}
      {data ? <EmailForms key={sessionKey} data={data} load={load} refreshing={loading} /> : loading && <Skeleton active />}
    </PullToRefresh>
  </FeaturePage>;
};
export default EmailSettings;
